import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import useSnapshotShare from '../../hooks/useSnapshotShare';
import useSnapshotHistory from '../../hooks/useSnapshotHistory';
import SnapshotToast from '../../components/SnapshotToast';
import HistoryDrawer from '../../components/HistoryDrawer';
import ConfirmationModal from '../../components/ConfirmationModal';
import ThoughtBubbleIcon from '../../components/ThoughtBubbleIcon';
import { Trash2, Share2, Check, Home, Archive, ChevronDown, ChevronUp, Plus, CornerDownLeft, Clock } from 'lucide-react';

// --- Sortable Item Component ---
const SortableTaskItem = ({ id, task, onToggle, onDelete }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 10 : 1,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            className="group flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-lg shadow-sm hover:shadow-md transition-all mb-2 cursor-grab active:cursor-grabbing"
        >
            {/* Custom Checkbox */}
            <button
                onClick={(e) => {
                    e.stopPropagation(); // Prevent drag start
                    onToggle(id);
                }}
                className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${task.completed
                    ? 'bg-cyan-500 border-cyan-500 text-white'
                    : 'border-slate-300 hover:border-cyan-400'
                    }`}
            >
                {task.completed && <Check size={14} strokeWidth={3} />}
            </button>

            {/* Task Text */}
            <span className={`flex-grow text-base font-medium transition-colors ${task.completed ? 'text-slate-400 line-through' : 'text-slate-700'
                }`}>
                {task.text}
            </span>

            {/* Delete Button (Hover Only) */}
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    onDelete(id);
                }}
                className="opacity-0 group-hover:opacity-100 p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-all"
                title="Delete Task"
            >
                <Trash2 size={18} />
            </button>
        </div>
    );
};

// --- Main Component ---
const LOCAL_STORAGE_KEY = 'taskBlink';

const TaskBlink = () => {
    const navigate = useNavigate();
    const [tasks, setTasks] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isArchiveOpen, setIsArchiveOpen] = useState(false);
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const [isClearModalOpen, setIsClearModalOpen] = useState(false);

    const [isLoaded, setIsLoaded] = useState(false);

    // Load from LocalStorage if no URL state
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (!params.get('s')) {
            try {
                const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
                if (stored) setTasks(JSON.parse(stored));
            } catch (e) {
                console.error("Failed to load tasks", e);
            }
        }
        setIsLoaded(true);
    }, []);

    // Save to LocalStorage
    useEffect(() => {
        if (isLoaded) {
            localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(tasks));
        }
    }, [tasks, isLoaded]);

    // Use shared snapshot hook (Green theme)
    const { handleShare, showToast, setShowToast } = useSnapshotShare(tasks, setTasks, 's');

    // Snapshot History Hook
    const { history, saveSnapshot, restoreSnapshot, deleteSnapshot, clearHistory } = useSnapshotHistory('taskBlink', tasks);

    const handleShareWithSnapshot = () => {
        saveSnapshot('Shared via Link');
        handleShare();
    };

    const handleRestore = (id) => {
        const data = restoreSnapshot(id);
        if (data) {
            setTasks(data);
            setIsHistoryOpen(false);
        }
    };

    // Sensors for Drag and Drop
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // --- Actions ---
    const submitTask = () => {
        if (inputValue.trim()) {
            const newTask = {
                id: `task_${Date.now()}`,
                text: inputValue.trim(),
                completed: false
            };
            setTasks(prev => [newTask, ...prev]);
            setInputValue('');
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') submitTask();
    };

    const handleToggleTask = (id) => {
        setTasks(prev => prev.map(t =>
            t.id === id ? { ...t, completed: !t.completed } : t
        ));
    };

    const handleDeleteTask = (id) => {
        setTasks(prev => prev.filter(t => t.id !== id));
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;

        if (active.id !== over.id) {
            setTasks((items) => {
                const oldIndex = items.findIndex(i => i.id === active.id);
                const newIndex = items.findIndex(i => i.id === over.id);
                return arrayMove(items, oldIndex, newIndex);
            });
        }
    };

    const clearList = () => {
        setIsClearModalOpen(true);
    };

    const confirmClearList = () => {
        setTasks([]);
        setIsClearModalOpen(false);
    };

    const activeTasks = tasks.filter(t => !t.completed);
    const archivedTasks = tasks.filter(t => t.completed);

    return (
        <div className="min-h-screen bg-slate-50 text-slate-800 font-sans selection:bg-cyan-100">
            {/* Header */}
            <header className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-md border-b border-slate-200 z-50">
                <div className="max-w-6xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
                    {/* Left: Brand */}
                    <div
                        className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity w-1/3"
                        onClick={() => navigate('/')}
                    >
                        <ThoughtBubbleIcon className="fill-blue-600 text-blue-600 shrink-0" size={24} />
                        <span className="text-sm md:text-xl font-bold md:font-extrabold text-blue-600 tracking-tight font-rounded">
                            Thoughtblink
                        </span>
                    </div>

                    {/* Center: Title */}
                    <div className="flex justify-center w-1/3">
                        <h1 className="text-lg md:text-xl font-bold bg-gradient-to-r from-green-500 to-emerald-600 bg-clip-text text-transparent">
                            To Do
                        </h1>
                    </div>

                    {/* Right: Actions */}
                    <div className="flex items-center justify-end gap-4 w-1/3">
                        <button
                            onClick={handleShareWithSnapshot}
                            className="p-2 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded-full transition-all"
                            title="Share List"
                        >
                            <Share2 size={20} />
                        </button>
                        <button
                            onClick={() => setIsHistoryOpen(true)}
                            className="p-2 text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded-full transition-all"
                            title="Version History"
                        >
                            <Clock size={20} />
                        </button>
                        <button
                            onClick={clearList}
                            className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-all"
                            title="Clear List"
                        >
                            <Trash2 size={20} />
                        </button>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="pt-24 pb-12 px-4 min-h-screen flex flex-col items-center">
                <div className="w-full max-w-2xl bg-white rounded-xl shadow-sm border border-slate-200 p-6 sm:p-8">

                    {/* Velocity Input */}
                    <div className="mb-8 relative z-40 w-full max-w-2xl mx-auto group">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <Plus className="text-slate-400" size={24} />
                        </div>
                        <input
                            type="text"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Capture a task..."
                            className="w-full pl-12 pr-12 py-4 bg-white border-2 border-slate-200 rounded-xl text-lg shadow-sm placeholder-slate-400 focus:border-slate-400 focus:outline-none transition-all"
                            autoFocus
                        />
                        <button
                            onClick={submitTask}
                            className="absolute inset-y-0 right-4 flex items-center justify-center text-slate-400 hover:text-blue-600 cursor-pointer transition-colors"
                            title="Add Task"
                        >
                            <CornerDownLeft size={20} />
                        </button>
                    </div>

                    {/* Active Task List */}
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        <SortableContext
                            items={activeTasks.map(t => t.id)}
                            strategy={verticalListSortingStrategy}
                        >
                            <div className="space-y-2 min-h-[100px]">
                                {activeTasks.length === 0 && archivedTasks.length === 0 && (
                                    <div className="text-center py-12 text-slate-300">
                                        <p>No tasks yet. Start typing above!</p>
                                    </div>
                                )}
                                {activeTasks.map(task => (
                                    <SortableTaskItem
                                        key={task.id}
                                        id={task.id}
                                        task={task}
                                        onToggle={handleToggleTask}
                                        onDelete={handleDeleteTask}
                                    />
                                ))}
                            </div>
                        </SortableContext>
                    </DndContext>

                    {/* Archive Section */}
                    {archivedTasks.length > 0 && (
                        <div className="mt-8 border-t border-slate-100 pt-6">
                            <button
                                onClick={() => setIsArchiveOpen(!isArchiveOpen)}
                                className="flex items-center gap-2 text-slate-400 hover:text-slate-600 transition-colors w-full mb-4 text-sm"
                            >
                                <Archive size={16} />
                                <span className="font-medium">Archive ({archivedTasks.length})</span>
                                {isArchiveOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                            </button>

                            {isArchiveOpen && (
                                <div className="space-y-2 animate-in fade-in slide-in-from-top-2 duration-300">
                                    {archivedTasks.map(task => (
                                        <div
                                            key={task.id}
                                            className="flex items-center gap-3 p-3 bg-slate-50/50 border border-slate-100 rounded-lg opacity-60 hover:opacity-100 transition-opacity"
                                        >
                                            <button
                                                onClick={() => handleToggleTask(task.id)}
                                                className="flex-shrink-0 w-5 h-5 rounded-full border-2 border-cyan-500 bg-cyan-500 text-white flex items-center justify-center"
                                            >
                                                <Check size={12} strokeWidth={3} />
                                            </button>
                                            <span className="flex-grow text-sm text-slate-400 line-through">
                                                {task.text}
                                            </span>
                                            <button
                                                onClick={() => handleDeleteTask(task.id)}
                                                className="p-1.5 text-slate-300 hover:text-red-400 transition-colors"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <SnapshotToast isVisible={showToast} onClose={() => setShowToast(false)} color="green" />
                <HistoryDrawer
                    isOpen={isHistoryOpen}
                    onClose={() => setIsHistoryOpen(false)}
                    history={history}
                    onRestore={handleRestore}
                    onDelete={deleteSnapshot}
                    onClearHistory={clearHistory}
                />
                <ConfirmationModal
                    isOpen={isClearModalOpen}
                    onClose={() => setIsClearModalOpen(false)}
                    onConfirm={confirmClearList}
                    title="Clear Task List?"
                    message="Are you sure you want to delete all tasks? This action cannot be undone."
                    confirmText="Yes, Clear All"
                />
            </main>
        </div>
    );
};

export default TaskBlink;
